/**
 * Pagina di dettaglio del thread di notifica SRP — CRA Art.14(2) (rework multi-step).
 *
 * Route: /enisa-reports/:id (il vecchio /srp/:id fa redirect qui)
 *
 * Gestisce il ciclo di vita del thread di notifica:
 * - Header compatto a breadcrumb «ENISA Reports › prodotto/release» (il link torna alla lista),
 *   poi in ordine: categoria ENISA (Actively Exploited Vulnerability / Severe Incident,
 *   modificabile via radio), avanzamento con i tre step (banner + timeline), elenco CVE
 *   collassabile (aperto di default).
 * - CVE del report: con VULNERABILITY_ANALYSIS si allegano (ricerca CVE note) e si rimuovono
 *   (con conferma); ogni modifica è tracciata nel log audit.
 * - Note libere dell'analista (persistite via PATCH /thread).
 * - Sequenza dei 3 step CRA Art.14(2): uno alla volta. «Registra» apre un pop-up per una nota
 *   testuale facoltativa; la conferma salva la nota e registra lo step come fatto
 *   (POST /record-step, tracciato in audit), poi si passa allo step successivo. Nulla viene
 *   inviato a ENISA: si traccia internamente lo step fatto a mano sul sito ENISA.
 * - Nessun bottone di risoluzione: dal dettaglio si può solo riaprire un report precedente
 *   (POST /resolve con resolution=open).
 * - Report precedenti (thread chiusi: risolto / non necessario): restano consultabili e
 *   modificabili; un banner lo segnala e permette di riaprire il thread. Ogni modifica è
 *   tracciata nel log audit (srp_report, entityId = id del report).
 * - Altri report della stessa release (precedenti o successivi), con link al dettaglio.
 * - Storico modifiche del report dal log audit (con permesso VIEW_AUDIT_LOG).
 */

import { ModalPortal } from '../../components/ModalPortal.js';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import { usePermissions } from '../../auth/usePermissions.js';
import {
  useSrpReport,
  useSrpReports,
  useSaveThread,
  useRecordStep,
  useResolveThread,
  useSetReportCategory,
  useDetachCve,
  useSlaTimers,
} from './hooks.js';
import {
  REPORT_CATEGORIES,
  REPORT_CATEGORY_LABEL,
  categoriaDiReport,
  type ReportCategory,
} from './category.js';
import type { NotificationStep, SrpCve, SrpReport, ThreadStatus } from './types.js';
import { SrpProgress, REGISTRA_STEP_LABEL, REGISTRA_STEP_TOOLTIP } from './SrpProgress.js';
import { SlaDetectionPanel } from './SlaDetectionPanel.js';
import { SrpStepUndo } from './SrpStepUndo.js';
import { finalRuleText } from './slaDeadlines.js';
import { timerForReport } from './slaAnchor.js';
import { SrpCveTable } from './SrpCveTable.js';
import { SrpCvePicker } from './SrpCvePicker.js';
import { SrpAuditHistory } from './SrpAuditHistory.js';
import { EnisaFormSection } from './enisaForm/EnisaFormSection.js';
import { LinkTicketOrigine, useTicketDiOrigine } from './originTickets.js';
import {
  statoEffettivo,
  isThreadChiuso,
  etichettaThread,
  tooltipThread,
  classeBadgeThread,
  dataBreve,
} from './threadStatus.js';
import { enisaReportPath, enisaReportsListPath } from './routes.js';
import styles from './Srp.module.css';

// ---------------------------------------------------------------------------
// Definizione step CRA Art.14(2)
// ---------------------------------------------------------------------------

interface StepDef {
  step: NotificationStep;
  label: string;
  title: string;
}

const STEP_DEFS: StepDef[] = [
  {
    step: 'early_warning',
    label: 'Early warning entro 24h, notifica preallarme',
    title: 'può anche non contenere alcun dettaglio',
  },
  {
    step: 'vulnerability',
    label: 'Notifica 72h',
    title:
      "Non necessaria se queste info sono già state comunicate nell'early warning. Informazioni generali, se disponibili, sul prodotto interessato / natura generale dello sfruttamento e della vulnerabilità in questione / eventuali misure correttive o di attenuazione adottate / eventuali misure correttive o di attenuazione che gli utilizzatori possono adottare / se si ritiene necessario, il grado di sensibilità delle informazioni notificate",
  },
  {
    step: 'final',
    label: 'Relazione finale, descrizione vulnerabilità',
    title:
      'una descrizione della vulnerabilità, compresi la sua gravità e il suo impatto / se disponibili, informazioni relative a qualsiasi soggetto malintenzionato che abbia sfruttato o che sfrutti la vulnerabilità / informazioni dettagliate relative alla aggiornamento di sicurezza o ad altre misure correttive messe a disposizione per porre rimedio alla vulnerabilità.',
  },
];

// ---------------------------------------------------------------------------
// Altri report della stessa release
// ---------------------------------------------------------------------------

/**
 * Elenco degli altri report ENISA dello stesso soggetto (stessa release): i thread di notifica
 * precedenti (chiusi) e l'eventuale thread corrente, dal più recente, con link al dettaglio.
 */
function AltriReportRelease({ report }: { report: SrpReport }) {
  const { data: reports } = useSrpReports();
  const altri = (reports ?? [])
    .filter((r) => r.subjectRef === report.subjectRef && r.id !== report.id)
    .sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta || b.id - a.id;
    });
  if (altri.length === 0) return null;

  return (
    <div className={styles.section ?? ''} data-testid="srp-altri-report">
      <p className={styles.sectionTitle ?? ''}>Altri report di questa release</p>
      <ul className={styles.simpleList ?? ''}>
        {altri.map((r) => {
          const stato = statoEffettivo(r);
          return (
            <li key={r.id}>
              <Link to={enisaReportPath(r.id)} data-testid={`link-report-${r.id}`}>
                Report #{r.id}
              </Link>{' '}
              <span className={styles.muted ?? ''}>creato il {dataBreve(r.createdAt)}</span>{' '}
              <span className={classeBadgeThread(stato)}>{etichettaThread(stato)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principale
// ---------------------------------------------------------------------------

export function SrpThreadDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const reportId = Number(id);
  const navigate = useNavigate();

  const { data: report, isLoading, isError, error } = useSrpReport(reportId);

  const [note, setNote] = useState('');
  /** Step per cui è aperto il pop-up di registrazione (null = chiuso) e relativa nota. */
  const [stepInRegistrazione, setStepInRegistrazione] = useState<NotificationStep | null>(null);
  const [notaStep, setNotaStep] = useState('');
  const [erroreNote, setErroreNote] = useState<string | null>(null);
  const [erroreRegistrazione, setErroreRegistrazione] = useState<string | null>(null);
  const [erroreRisoluzione, setErroreRisoluzione] = useState<string | null>(null);

  // Timer SLA del report: ancora del clock CRA (correggibile dall'analista).
  const { data: timers } = useSlaTimers();

  const saveThreadMutation = useSaveThread(reportId);
  const recordStepMutation = useRecordStep(reportId);
  const resolveThreadMutation = useResolveThread(reportId);
  const setCategoryMutation = useSetReportCategory(reportId);
  const [erroreCategoria, setErroreCategoria] = useState<string | null>(null);
  const detachCveMutation = useDetachCve(reportId);
  /** CVE per cui è aperta la conferma di rimozione (null = chiusa). */
  const [cveDaRimuovere, setCveDaRimuovere] = useState<string | null>(null);
  const [erroreRimozioneCve, setErroreRimozioneCve] = useState<string | null>(null);
  const { can } = usePermissions();
  const puoModificareCve = can('VULNERABILITY_ANALYSIS');
  const ticketDiOrigine = useTicketDiOrigine();

  // Seed delle note locali dal payload, quando il report viene caricato/aggiornato
  useEffect(() => {
    if (report) {
      setNote(report.payload.analystNotes ?? '');
    }
  }, [report]);

  if (isLoading) {
    return (
      <div data-testid="srp-dettaglio">
        <p className={styles.loadingMsg} data-testid="srp-dettaglio-loading">
          Caricamento thread SRP...
        </p>
      </div>
    );
  }

  if (isError || !report) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div data-testid="srp-dettaglio">
        <nav className={styles.breadcrumb} aria-label="Percorso" data-testid="srp-breadcrumb">
          <Link to={enisaReportsListPath()} className={styles.breadcrumbLink}>
            ENISA Reports
          </Link>
          <span className={styles.breadcrumbSep} aria-hidden="true">
            ›
          </span>
          <span className={styles.breadcrumbTitle}>Report #{id}</span>
        </nav>
        <div className={styles.errorMsg} data-testid="srp-dettaglio-error">
          {notFound
            ? 'Thread SRP non trovato.'
            : error instanceof ApiError
              ? error.message
              : 'Errore nel caricamento del thread SRP.'}
        </div>
      </div>
    );
  }

  const notifiche = report.payload.notifications ?? [];
  const prodotto = report.payload.affectedProduct;
  // CVE effettive: la lista del report, o — report storici a CVE singola — la primaria.
  const cves: SrpCve[] =
    report.payload.cves ??
    (report.payload.vulnerability
      ? [
          {
            cveId: report.payload.vulnerability.cveId,
            severity: report.payload.vulnerability.severity,
          },
        ]
      : []);
  const statoThread = statoEffettivo(report);
  const chiuso = isThreadChiuso(report);
  const timer = timerForReport(report, timers);
  const origine = ticketDiOrigine(report);

  function handleSalvaNote() {
    setErroreNote(null);
    saveThreadMutation.mutate(
      { analystNotes: note },
      {
        onError: (err) => {
          setErroreNote(err instanceof ApiError ? err.message : (err.message ?? 'Errore'));
        },
      },
    );
  }

  /** Apre il pop-up di registrazione per lo step (nota vuota). */
  function apriRegistrazione(step: NotificationStep) {
    setErroreRegistrazione(null);
    setNotaStep('');
    setStepInRegistrazione(step);
  }

  /**
   * CTA del banner "prossima azione" (SrpProgress): apre la registrazione dello step attivo.
   */
  function handleVaiAlloStep(step: NotificationStep) {
    const idx = STEP_DEFS.findIndex((d) => d.step === step);
    if (idx === -1 || notifiche.some((n) => n.step === step)) return;
    apriRegistrazione(step);
  }

  /**
   * Conferma dal pop-up: salva la nota (facoltativa) e registra lo step come fatto; al successo
   * il pop-up si chiude e il thread passa allo step successivo (ricalcolato dal report).
   */
  async function handleConfermaRegistrazione() {
    const step = stepInRegistrazione;
    if (!step) return;
    setErroreRegistrazione(null);
    try {
      await recordStepMutation.mutateAsync({ step, note: notaStep.trim() || undefined });
      setStepInRegistrazione(null);
      setNotaStep('');
    } catch (err) {
      setErroreRegistrazione(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Errore durante la registrazione dello step',
      );
    }
  }

  /** Conferma della rimozione: DELETE della CVE; al successo la conferma si chiude. */
  function handleConfermaRimozioneCve() {
    const cveId = cveDaRimuovere;
    if (!cveId) return;
    setErroreRimozioneCve(null);
    detachCveMutation.mutate(cveId, {
      onSuccess: () => {
        setCveDaRimuovere(null);
      },
      onError: (err) => {
        setErroreRimozioneCve(err instanceof ApiError ? err.message : (err.message ?? 'Errore'));
      },
    });
  }

  function handleCambiaCategoria(category: ReportCategory) {
    setErroreCategoria(null);
    setCategoryMutation.mutate(category, {
      onError: (err) => {
        setErroreCategoria(err instanceof ApiError ? err.message : (err.message ?? 'Errore'));
      },
    });
  }

  function handleRisolvi(resolution: ThreadStatus) {
    setErroreRisoluzione(null);
    resolveThreadMutation.mutate(resolution, {
      onSuccess: () => {
        // Riaprire un report precedente lo riporta tra gli aperti: si resta sul dettaglio.
        if (resolution !== 'open') navigate(enisaReportsListPath());
      },
      onError: (err) => {
        setErroreRisoluzione(err instanceof ApiError ? err.message : (err.message ?? 'Errore'));
      },
    });
  }

  return (
    <div data-testid="srp-dettaglio">
      {/* Intestazione compatta a breadcrumb: ENISA Reports › prodotto/release (+ stato) */}
      <nav className={styles.breadcrumb} aria-label="Percorso" data-testid="srp-breadcrumb">
        <Link
          to={enisaReportsListPath(chiuso ? 'precedenti' : 'aperti')}
          className={styles.breadcrumbLink}
          data-testid="srp-torna-lista"
        >
          ENISA Reports
        </Link>
        <span className={styles.breadcrumbSep} aria-hidden="true">
          ›
        </span>
        <h1 className={styles.breadcrumbTitle}>
          {prodotto ? `${prodotto.name} v${prodotto.releaseVersion}` : report.subjectRef}
        </h1>
        <span
          className={classeBadgeThread(statoThread)}
          title={tooltipThread(statoThread)}
          data-testid="srp-stato-thread"
        >
          {etichettaThread(statoThread)}
        </span>
        <span className={styles.pageSubtitle}>
          Report #{report.id} — creato il {dataBreve(report.createdAt)}
        </span>
        {origine.length > 0 && (
          <span className={styles.pageSubtitle}>
            — da <LinkTicketOrigine reportId={report.id} ticketIds={origine} />
          </span>
        )}
      </nav>

      {/* Modulo ENISA: campi del sito ENISA compilati qui ed esportati in JSON (pop-up) */}
      <EnisaFormSection report={report} />

      {/* Report precedente (thread chiuso): resta modificabile, con riapertura */}
      {chiuso && (
        <div className={styles.closedBanner} data-testid="srp-report-chiuso" role="status">
          <span>
            Report precedente — thread <strong>{etichettaThread(statoThread)}</strong>. Puoi ancora
            correggerlo: ogni modifica è registrata nel log audit.
          </span>
          <button
            type="button"
            className={styles.btnSecondary}
            data-testid="riapri"
            disabled={resolveThreadMutation.isPending}
            onClick={() => {
              handleRisolvi('open');
            }}
          >
            Riapri thread
          </button>
          {erroreRisoluzione && (
            <span className={styles.inlineMutError} data-testid="errore-risoluzione">
              {erroreRisoluzione}
            </span>
          )}
        </div>
      )}

      {/* Categoria ENISA: Actively Exploited Vulnerability / Severe Incident */}
      <div className={styles.section ?? ''} data-testid="srp-categoria">
        <p className={styles.sectionTitle ?? ''}>Categoria</p>
        <fieldset className={styles.categoriaRadioGroup} disabled={setCategoryMutation.isPending}>
          <legend className="sr-only" style={{ position: 'absolute', left: '-9999px' }}>
            Categoria del report ENISA
          </legend>
          {REPORT_CATEGORIES.map((categoria) => (
            <label key={categoria} className={styles.categoriaRadioOption}>
              <input
                type="radio"
                name="categoria-report"
                value={categoria}
                checked={categoriaDiReport(report.payload) === categoria}
                onChange={() => {
                  handleCambiaCategoria(categoria);
                }}
                data-testid={`categoria-radio-${categoria}`}
              />
              {REPORT_CATEGORY_LABEL[categoria]}
            </label>
          ))}
        </fieldset>
        {erroreCategoria && (
          <span className={styles.inlineMutError} data-testid="errore-categoria">
            {erroreCategoria}
          </span>
        )}
      </div>

      {/* Avanzamento notifica CRA Art.14(2): banner prossima azione + stepper 24h/72h/finale */}
      <SrpProgress report={report} timer={timer} onActionClick={handleVaiAlloStep} />
      <SrpStepUndo report={report} />

      <details className={styles.section ?? ''} data-testid="srp-cve-list" open>
        <summary className={styles.sectionSummary}>
          CVE{' '}
          <span className={styles.muted ?? ''} data-testid="srp-cve-conteggio">
            ({cves.length})
          </span>
        </summary>
        {cves.length > 0 ? (
          <SrpCveTable
            cves={cves}
            {...(puoModificareCve
              ? {
                  onRimuovi: (cveId: string) => {
                    setErroreRimozioneCve(null);
                    setCveDaRimuovere(cveId);
                  },
                  rimozioneDisabilitata: detachCveMutation.isPending,
                }
              : {})}
          />
        ) : (
          <p className={styles.muted ?? ''} data-testid="srp-cve-vuoto">
            Nessuna CVE associata.
          </p>
        )}
        {puoModificareCve && (
          <SrpCvePicker reportId={report.id} giaPresenti={cves.map((c) => c.cveId)} />
        )}
      </details>

      {/* Note interne analista */}
      <div className={styles.section ?? ''}>
        <p className={styles.sectionTitle ?? ''}>Note interne analista</p>
        <textarea
          data-testid="srp-note"
          className={styles.formInput}
          rows={4}
          style={{ width: '100%', resize: 'vertical' }}
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
          }}
        />
        <div className={styles.formActions}>
          <button
            type="button"
            className={styles.btnPrimary}
            data-testid="salva-note"
            disabled={saveThreadMutation.isPending}
            onClick={handleSalvaNote}
          >
            {saveThreadMutation.isPending ? 'Salvataggio...' : 'Salva note'}
          </button>
          {erroreNote && (
            <span className={styles.inlineMutError} data-testid="errore-note">
              {erroreNote}
            </span>
          )}
        </div>
      </div>

      {/* Ancora del clock CRA: istante di rilevazione, correggibile con motivazione */}
      <SlaDetectionPanel timer={timer} />

      <AltriReportRelease report={report} />

      <SrpAuditHistory reportId={report.id} timerId={timer?.id} />

      {/* Pop-up di registrazione dello step: nota testuale facoltativa */}
      {stepInRegistrazione && (
        <ModalPortal onClose={() => setStepInRegistrazione(null)}>
          <div className={styles.dialogOverlay} data-testid="dialog-registra-step" role="dialog">
            <div className={styles.dialogBox}>
              <p className={styles.dialogTitle} title={REGISTRA_STEP_TOOLTIP}>
                {REGISTRA_STEP_LABEL}
              </p>
              <p className={styles.dialogBody}>
                {STEP_DEFS.find((d) => d.step === stepInRegistrazione)?.label}
                {stepInRegistrazione === 'final' ? ` (${finalRuleText(report)})` : ''}. Puoi
                aggiungere una nota (facoltativa): verrà salvata con lo step e il thread passerà
                allo step successivo. Confermando, il timer SLA dello step si ferma ora (se lo step
                non viene poi inviato a ENISA, annulla la registrazione per farlo ripartire).
              </p>
              <p className={styles.muted ?? ''} data-testid="registra-step-suggerimento">
                {STEP_DEFS.find((d) => d.step === stepInRegistrazione)?.title}
              </p>
              <textarea
                className={styles.stepNoteInput}
                data-testid="registra-step-nota"
                rows={4}
                placeholder="Note (facoltative)"
                value={notaStep}
                onChange={(e) => {
                  setNotaStep(e.target.value);
                }}
                autoFocus
              />
              {erroreRegistrazione && (
                <span className={styles.inlineMutError} data-testid="errore-registra-step">
                  {erroreRegistrazione}
                </span>
              )}
              <div className={styles.dialogActions}>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  data-testid="registra-step-annulla"
                  disabled={recordStepMutation.isPending}
                  onClick={() => {
                    setStepInRegistrazione(null);
                  }}
                >
                  Annulla
                </button>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  data-testid="registra-step-conferma"
                  title={REGISTRA_STEP_TOOLTIP}
                  disabled={recordStepMutation.isPending}
                  onClick={() => void handleConfermaRegistrazione()}
                >
                  {recordStepMutation.isPending ? 'Registrazione…' : REGISTRA_STEP_LABEL}
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}

      {/* Conferma rimozione di una CVE dal report */}
      {cveDaRimuovere && (
        <ModalPortal onClose={() => setCveDaRimuovere(null)}>
          <div className={styles.dialogOverlay} data-testid="dialog-rimuovi-cve" role="dialog">
            <div className={styles.dialogBox}>
              <p className={styles.dialogTitle}>Rimuovi CVE</p>
              <p className={styles.dialogBody}>
                Rimuovere <strong style={{ fontFamily: 'monospace' }}>{cveDaRimuovere}</strong> da
                questo report? La modifica è registrata nel log audit; la CVE si può allegare di
                nuovo.
              </p>
              {erroreRimozioneCve && (
                <span className={styles.inlineMutError} data-testid="errore-rimuovi-cve">
                  {erroreRimozioneCve}
                </span>
              )}
              <div className={styles.dialogActions}>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  data-testid="rimuovi-cve-annulla"
                  disabled={detachCveMutation.isPending}
                  onClick={() => {
                    setCveDaRimuovere(null);
                  }}
                >
                  Annulla
                </button>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  data-testid="rimuovi-cve-conferma"
                  disabled={detachCveMutation.isPending}
                  onClick={handleConfermaRimozioneCve}
                >
                  {detachCveMutation.isPending ? 'Rimozione…' : 'Rimuovi'}
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
}
